import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Modal, Form, Row, Col, Button, Alert, Table, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { fmtMoney } from '../lib/format.js';

const EMPTY_NEW_CUSTOMER = {
  name: '', contact_person: '', contact_email: '', contact_phone: '',
  address_street: '', address_zip: '', address_city: '', address_country: 'DE',
  vat_id: '', website: ''
};

export default function NewOrderModal({ show, onClose }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [mode, setMode] = useState('existing');
  const [customerNumber, setCustomerNumber] = useState('');
  const [newCustomer, setNewCustomer] = useState(EMPTY_NEW_CUSTOMER);
  const [orderName, setOrderName] = useState('');
  const [note, setNote] = useState('');
  const [commitmentMonths, setCommitmentMonths] = useState(1);
  const [autoRenew, setAutoRenew] = useState(true);
  const [discountCode, setDiscountCode] = useState('');
  // selected: { [feature_id]: quantity }
  const [selected, setSelected] = useState({});
  const [error, setError] = useState(null);

  const customers = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => api.get('/customers', { params: { limit: 1000 } }).then(r => r.data.items),
    enabled: show
  });

  const features = useQuery({
    queryKey: ['features-with-tiers'],
    queryFn: () => api.get('/features', { params: { with_tiers: true } }).then(r => r.data.items),
    enabled: show
  });

  const commitmentTerms = useQuery({
    queryKey: ['commitment-terms'],
    queryFn: () => api.get('/commitment-terms').then(r => r.data.items || []),
    enabled: show
  });

  const activeFeatures = useMemo(
    () => (features.data || []).filter(f => f.is_active),
    [features.data]
  );

  const resolvedExistingCustomer = useMemo(() => {
    if (mode !== 'existing' || !customerNumber.trim()) return null;
    return (customers.data || []).find(c =>
      c.customer_number?.toLowerCase() === customerNumber.trim().toLowerCase()
    );
  }, [mode, customerNumber, customers.data]);

  const toggleFeature = (f) => {
    setSelected(s => {
      if (s[f.id] !== undefined) {
        const { [f.id]: _, ...rest } = s;
        return rest;
      }
      const defaultQty = f.feature_type === 'quota' ? (f.default_value?.limit || 1) : 1;
      return { ...s, [f.id]: defaultQty };
    });
  };

  const setQty = (id, qty) => setSelected(s => ({ ...s, [id]: Math.max(1, parseInt(qty, 10) || 1) }));

  const reset = () => {
    setMode('existing');
    setCustomerNumber('');
    setNewCustomer(EMPTY_NEW_CUSTOMER);
    setOrderName('');
    setNote('');
    setCommitmentMonths(1);
    setAutoRenew(true);
    setDiscountCode('');
    setSelected({});
    setError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const create = useMutation({
    mutationFn: () => {
      const items = Object.entries(selected).map(([feature_id, quantity]) => ({ feature_id, quantity }));
      if (items.length === 0) throw new Error('Mindestens 1 Feature waehlen');

      const customer = mode === 'existing'
        ? { mode: 'existing', customer_number: customerNumber.trim() }
        : { mode: 'new', data: newCustomer };

      if (mode === 'existing' && !customerNumber.trim()) throw new Error('Kundennummer erforderlich');
      if (mode === 'new' && !newCustomer.name.trim()) throw new Error('Name erforderlich');

      const body = {
        customer,
        items,
        commitment_term_months: commitmentMonths,
        auto_renew: autoRenew,
        discount_code: discountCode.trim() || null,
        name: orderName.trim() || null,
        note: note.trim() || null
      };
      return api.post('/orders', body).then(r => r.data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['customers-all'] });
      handleClose();
      navigate(`/orders/${data.order.id}`);
    },
    onError: (err) => {
      // Discount-Pre-Flight ist nur Anzeige — Backend (licenseConfiguratorService)
      // validiert nochmal final. Race wenn Code zwischen Pre-Flight + Submit
      // verbraucht wird: Backend wirft → Error hier anzeigen.
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 502 && data?.message?.includes('Kontor')) {
        setError('Kontor nicht erreichbar — bitte spaeter versuchen.');
      } else if (status === 500 || data?.details?.stage === 'configure') {
        setError('Bestellung evtl. erstellt aber License-Materialize fehlgeschlagen. '
          + 'Bitte Bestellungs-Liste pruefen oder Operator kontaktieren.');
      } else {
        setError(data?.message || err.message || JSON.stringify(data));
      }
    }
  });

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Neue Bestellung anlegen</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        <h6 className="mt-2">1. Kunde</h6>
        <Form.Group className="mb-3">
          <Form.Check inline type="radio" id="mode-existing" name="cust-mode" label="Bestehender Kunde"
            checked={mode === 'existing'} onChange={() => setMode('existing')} />
          <Form.Check inline type="radio" id="mode-new" name="cust-mode" label="Neuer Kunde"
            checked={mode === 'new'} onChange={() => setMode('new')} />
        </Form.Group>

        {mode === 'existing' && (
          <Row className="mb-3">
            <Col md={5}>
              <Form.Label className="small mb-1">Kundennummer</Form.Label>
              <Form.Control type="text" placeholder="K-2026-0001"
                value={customerNumber} onChange={e => setCustomerNumber(e.target.value)} />
            </Col>
            <Col md={7} className="d-flex align-items-end">
              {resolvedExistingCustomer ? (
                <div className="small">
                  <Icon icon="tabler:check" className="text-success me-1" />
                  <span className="fw-semibold">{resolvedExistingCustomer.name}</span>
                  {resolvedExistingCustomer.contact_email && (
                    <span className="text-muted ms-2">{resolvedExistingCustomer.contact_email}</span>
                  )}
                </div>
              ) : customerNumber.trim() && (
                <div className="small text-warning">
                  <Icon icon="tabler:alert-triangle" className="me-1" />
                  Nicht gefunden (wird bei Submit validiert).
                </div>
              )}
            </Col>
          </Row>
        )}

        {mode === 'new' && (
          <Row className="mb-3 g-2">
            <Col md={6}>
              <Form.Label className="small mb-1">Name *</Form.Label>
              <Form.Control value={newCustomer.name}
                onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small mb-1">Ansprechpartner</Form.Label>
              <Form.Control value={newCustomer.contact_person}
                onChange={e => setNewCustomer({ ...newCustomer, contact_person: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small mb-1">E-Mail</Form.Label>
              <Form.Control type="email" value={newCustomer.contact_email}
                onChange={e => setNewCustomer({ ...newCustomer, contact_email: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small mb-1">Telefon</Form.Label>
              <Form.Control value={newCustomer.contact_phone}
                onChange={e => setNewCustomer({ ...newCustomer, contact_phone: e.target.value })} />
            </Col>
            <Col md={8}>
              <Form.Label className="small mb-1">Strasse</Form.Label>
              <Form.Control value={newCustomer.address_street}
                onChange={e => setNewCustomer({ ...newCustomer, address_street: e.target.value })} />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">PLZ</Form.Label>
              <Form.Control value={newCustomer.address_zip}
                onChange={e => setNewCustomer({ ...newCustomer, address_zip: e.target.value })} />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Land</Form.Label>
              <Form.Control value={newCustomer.address_country}
                onChange={e => setNewCustomer({ ...newCustomer, address_country: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small mb-1">Stadt</Form.Label>
              <Form.Control value={newCustomer.address_city}
                onChange={e => setNewCustomer({ ...newCustomer, address_city: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small mb-1">USt-ID</Form.Label>
              <Form.Control value={newCustomer.vat_id}
                onChange={e => setNewCustomer({ ...newCustomer, vat_id: e.target.value })} />
            </Col>
          </Row>
        )}

        <h6 className="mt-4">2. Items (Features)</h6>
        {features.isLoading && <div className="text-muted small">Lade Features…</div>}
        {activeFeatures.length > 0 && (
          <Table size="sm" className="mb-3">
            <thead><tr><th></th><th>Feature</th><th>Typ</th><th>Preis</th><th>Menge</th></tr></thead>
            <tbody>
              {activeFeatures.map(f => {
                const isSel = selected[f.id] !== undefined;
                const showQty = isSel && (f.pricing_model === 'per_unit' || f.pricing_model === 'tiered' || f.feature_type === 'quota');
                return (
                  <tr key={f.id}>
                    <td><Form.Check type="checkbox" checked={isSel} onChange={() => toggleFeature(f)} /></td>
                    <td><span className="fw-semibold">{f.name}</span>
                      <div className="small text-muted">{f.key}</div></td>
                    <td><Badge bg="" className="badge-soft-secondary">{f.feature_type}</Badge></td>
                    <td className="small">{fmtMoney(f.price_cents)} / {f.billing_period}</td>
                    <td style={{ width: 100 }}>
                      {showQty && (
                        <Form.Control type="number" size="sm" min={1}
                          value={selected[f.id]} onChange={e => setQty(f.id, e.target.value)} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <h6 className="mt-4">3. Optionen</h6>
        <Row className="g-2 mb-2">
          <Col md={6}>
            <Form.Label className="small mb-1">Bestell-Name (optional)</Form.Label>
            <Form.Control value={orderName} onChange={e => setOrderName(e.target.value)}
              placeholder="z.B. Erstauftrag 2026" />
          </Col>
          <Col md={3}>
            <Form.Label className="small mb-1">Mindestlaufzeit</Form.Label>
            <Form.Select value={commitmentMonths} onChange={e => setCommitmentMonths(parseInt(e.target.value, 10))}>
              {(commitmentTerms.data || [{ term_months: 1, label: '1 Monat' }]).map(t => (
                <option key={t.term_months} value={t.term_months}>
                  {t.label || `${t.term_months} Monate`} {t.discount_percent > 0 && `(-${t.discount_percent}%)`}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <Form.Check type="switch" id="auto-renew" label="Auto-Verlaengerung"
              checked={autoRenew} onChange={e => setAutoRenew(e.target.checked)} />
          </Col>
        </Row>
        <Row className="g-2 mb-2">
          <Col md={6}>
            <Form.Label className="small mb-1">Rabattcode (optional)</Form.Label>
            <Form.Control value={discountCode} onChange={e => setDiscountCode(e.target.value)} />
          </Col>
          <Col md={6}>
            <Form.Label className="small mb-1">Notiz (optional)</Form.Label>
            <Form.Control value={note} onChange={e => setNote(e.target.value)} />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>Abbrechen</Button>
        <Button onClick={() => create.mutate()} disabled={create.isLoading}>
          {create.isLoading ? 'Erstelle…' : 'Bestellung anlegen'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
